import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "@/api";

const inputCls =
  "w-full rounded-lg border-2 border-[#F5F2EB] bg-[#141414] px-4 py-3 text-sm font-mono text-[#F5F2EB] placeholder-[#F5F2EB]/40 transition-shadow focus:shadow-[3px_3px_0_#F5F2EB] focus:outline-none";

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — talk soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="grid gap-16 border-t-2 border-[#F5F2EB] px-6 py-24 md:px-12 md:py-28 lg:grid-cols-2 lg:px-20">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
      >
        <p className="mb-4 inline-block rounded-full border-2 border-[#F5F2EB] bg-[#141414] px-4 py-1 text-[10px] font-mono font-medium uppercase tracking-[0.25em]">
          04 — Contact & About
        </p>
        <h2 data-testid="contact-heading" className="font-serif font-black uppercase leading-tight tracking-tight text-4xl sm:text-5xl">
          Every inquiry is read personally.
        </h2>
        <p className="mt-8 max-w-md text-sm leading-relaxed text-[#F5F2EB]/60">
          JFLY is a one-person archive of pre-loved clothing. Pieces are sourced, cleaned, and photographed by hand, then
          listed here until they find their next owner.
        </p>
        <p className="mt-4 max-w-md text-sm leading-relaxed text-[#F5F2EB]/60">
          Questions about sizing, condition, or shipping? Want to sell a piece into the archive? Send a message — expect
          a reply within a day or two.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href="https://www.tiktok.com/@jitclips2"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-tiktok-link"
            className="rounded-full border-2 border-[#F5F2EB] bg-[#141414] px-5 py-2 text-[10px] font-mono font-semibold uppercase tracking-[0.2em] transition-all hover:-translate-y-0.5 hover:shadow-[3px_3px_0_#F5F2EB]"
          >
            TikTok — @jitclips2
          </a>
        </div>
      </motion.div>

      <motion.form
        onSubmit={submit}
        data-testid="contact-form"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, delay: 0.15 }}
        className="space-y-4 self-center rounded-2xl border-2 border-[#F5F2EB] bg-[#141414] p-6 shadow-[6px_6px_0_#F5F2EB] md:p-8"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <input
            required
            data-testid="contact-name-input"
            placeholder="Your name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={inputCls}
          />
          <input
            required
            type="email"
            data-testid="contact-email-input"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={inputCls}
          />
        </div>
        <textarea
          required
          rows={6}
          data-testid="contact-message-input"
          placeholder="Your message"
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          className={inputCls}
        />
        <button
          disabled={sending}
          data-testid="contact-submit-button"
          className="w-full rounded-full border-2 border-[#F5F2EB] bg-[#FF3B1F] px-6 py-3 text-xs font-mono font-medium uppercase tracking-[0.2em] text-white shadow-[4px_4px_0_#F5F2EB] transition-transform hover:-translate-y-0.5 disabled:opacity-50 sm:w-auto"
        >
          {sending ? "Sending…" : "Send Message"}
        </button>
      </motion.form>
    </section>
  );
};
